import { create } from 'zustand';

import type { ExperimentParamsProp } from './types';

type ExperimentsMap = Record<string, ExperimentParamsProp>;

type DynamicConfigsMap = Record<string, Record<string, unknown>>;

/* store */
export const store = create<{
  experiments: ExperimentsMap;
  gates: Record<string, boolean>;
  dynamicConfigs: DynamicConfigsMap;
}>()(() => ({
  experiments: {},
  gates: {},
  dynamicConfigs: {},
}));

/* actions */
export const addExperiment = (name: string, params: ExperimentParamsProp) =>
  store.setState((state) =>
    state.experiments[name] !== undefined ? state : { experiments: { ...state.experiments, [name]: params } }
  );

export const initExperimentValues = (name: string, params: ExperimentParamsProp) =>
  store.setState((state) => ({
    experiments: { ...state.experiments, [name]: { ...state.experiments[name], ...params } },
  }));

export const updateExperimentParam = (name: string, paramKey: string, paramValue: string | number | boolean) =>
  store.setState((state) => ({
    experiments: {
      ...state.experiments,
      [name]: { ...state.experiments[name], [paramKey]: paramValue },
    },
  }));

export const addGate = (name: string, enabled: boolean) =>
  store.setState((state) => (state.gates[name] !== undefined ? state : { gates: { ...state.gates, [name]: enabled } }));

export const updateGate = (name: string, enabled: boolean) =>
  store.setState((state) => ({
    gates: { ...state.gates, [name]: enabled },
  }));

export const addDynamicConfig = (name: string, config: Record<string, unknown>) =>
  store.setState((state) => ({
    dynamicConfigs: { ...state.dynamicConfigs, [name]: config },
  }));

export const updateDynamicConfig = (name: string, config: Record<string, unknown>) =>
  store.setState((state) => ({
    dynamicConfigs: { ...state.dynamicConfigs, [name]: config },
  }));

/* hooks */
export function useExperiments(): ExperimentsMap;

export function useExperiments<K extends keyof ExperimentsMap>(name: K): ExperimentsMap[K];

export function useExperiments(name?: string) {
  return store((state) => (name ? state.experiments[name] : state.experiments));
}

export function useGates(): Record<string, boolean>;

export function useGates(name: string): boolean;

export function useGates(name?: string) {
  return store((state) => (name ? state.gates[name] : state.gates));
}

export function useDynamicConfigs(): DynamicConfigsMap;

export function useDynamicConfigs(name: string): Record<string, unknown>;

export function useDynamicConfigs(name?: string) {
  return store((state) => (name ? state.dynamicConfigs[name] : state.dynamicConfigs));
}
